import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../services/api';
import './InterviewSetup.css';

const InterviewSetup = ({ onStartInterview }) => {
  const { accessToken, sessionId, logout, email } = useAuth();
  const [applications, setApplications] = useState([]);
  const [jobs, setJobs] = useState({});
  const [selectedApplicationId, setSelectedApplicationId] = useState('');
  const [selectedInterviewRoundIndex, setSelectedInterviewRoundIndex] = useState('');
  const [resumableEnabled, setResumableEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [starting, setStarting] = useState(false);

  // Get the selected application and its job details
  // Backend returns applicationId, not _id
  const selectedApplication = applications.find(app => app.applicationId === selectedApplicationId);
  const selectedJob = selectedApplication ? jobs[selectedApplication.jobId] : null;
  const interviewRounds = useMemo(() => selectedJob?.interviewRounds || [], [selectedJob]);
  const selectedInterviewRound = selectedInterviewRoundIndex !== '' ? interviewRounds[parseInt(selectedInterviewRoundIndex, 10)] : null;

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // Fetch user's applications
      const applicationsData = await apiService.getMyApplications(accessToken, sessionId);
      setApplications(applicationsData || []);

      // Fetch job details for each application
      const jobsMap = {};
      const uniqueJobIds = [...new Set(applicationsData.map(app => app.jobId))];

      await Promise.all(
        uniqueJobIds.map(async (jobId) => {
          try {
            const jobData = await apiService.getJobById(jobId, accessToken, sessionId);
            jobsMap[jobId] = jobData;
          } catch (err) {
            console.error(`Failed to fetch job ${jobId}:`, err);
          }
        })
      );

      setJobs(jobsMap);

      // Auto-select first application if available
      if (applicationsData && applicationsData.length > 0) {
        setSelectedApplicationId(applicationsData[0].applicationId);
      }
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to load data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [accessToken, sessionId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Reset interview round selection when application changes
  useEffect(() => {
    setSelectedInterviewRoundIndex('');
  }, [selectedApplicationId]);

  // Auto-select first interview round when job is loaded
  useEffect(() => {
    if (interviewRounds.length > 0 && selectedInterviewRoundIndex === '') {
      setSelectedInterviewRoundIndex('0');
    }
  }, [interviewRounds, selectedInterviewRoundIndex]);

  const handleStartInterview = async () => {
    if (!selectedApplicationId) {
      setError('Please select an application');
      return;
    }

    if (selectedInterviewRoundIndex === '') {
      setError('Please select an interview round');
      return;
    }

    setStarting(true);
    setError(null);

    try {
      const application = applications.find(app => app.applicationId === selectedApplicationId);
      const roundIndex = parseInt(selectedInterviewRoundIndex, 10);

      // Call the parent handler with jobId, applicationId, and interviewRoundIndex
      await onStartInterview(
        application.jobId,
        selectedApplicationId,
        roundIndex,
        resumableEnabled
      );
    } catch (err) {
      console.error('Failed to start interview:', err);
      setError(err.message || 'Failed to start interview');
      setStarting(false);
    }
  };

  // Get interview type display name
  const getInterviewTypeDisplay = (type) => {
    const types = {
      'technical': 'Technical',
      'behavioral': 'Behavioral',
      'culture_fit': 'Culture Fit',
      'cultural': 'Culture Fit',
      'hr': 'HR Round',
      'system_design': 'System Design',
      'coding': 'Coding Round'
    };
    return types[type] || type;
  };

  // Format interview types array for display
  const formatInterviewTypes = (interviewTypes) => {
    if (!interviewTypes || interviewTypes.length === 0) {
      return 'General Interview';
    }
    return interviewTypes.map(type => getInterviewTypeDisplay(type)).join(', ');
  };

  // Get status badge style
  const getStatusStyle = (status) => {
    const styles = {
      'pending': { background: '#fff3e0', color: '#e65100', text: 'Pending' },
      'in_review': { background: '#e3f2fd', color: '#1565c0', text: 'In Review' },
      'shortlisted': { background: '#e8f5e9', color: '#2e7d32', text: 'Shortlisted' },
      'rejected': { background: '#ffebee', color: '#c62828', text: 'Rejected' },
      'hired': { background: '#e8f5e9', color: '#2e7d32', text: 'Hired' }
    };
    return styles[status] || { background: '#f5f5f5', color: '#616161', text: status };
  };

  if (loading) {
    return (
      <div className="interview-setup">
        <div className="setup-container">
          <h2>Loading your applications...</h2>
          <div className="loading-spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="interview-setup">
      <div className="setup-container">
        <div className="setup-header">
          <div>
            <h1>Start Your Interview</h1>
            <p className="subtitle">
              Select your job application and interview round to begin
            </p>
          </div>
          <div className="user-info">
            <span className="user-email">{email}</span>
            <button className="logout-btn-setup" onClick={logout}>
              Logout
            </button>
          </div>
        </div>

        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        {applications.length === 0 ? (
          <div className="no-applications">
            <h3>No Applications Found</h3>
            <p>You haven't applied to any jobs yet. Please apply to a job first to start an interview.</p>
          </div>
        ) : (
          <>
            <div className="selection-section">
              {/* Application Selector */}
              <div className="form-group">
                <label htmlFor="application-select">Select Your Application:</label>
                <select
                  id="application-select"
                  value={selectedApplicationId}
                  onChange={(e) => setSelectedApplicationId(e.target.value)}
                  disabled={starting}
                >
                  <option value="">-- Select an Application --</option>
                  {applications.map((application) => {
                    const job = jobs[application.jobId];
                    const statusStyle = getStatusStyle(application.status);
                    return (
                      <option key={application.applicationId} value={application.applicationId}>
                        {job?.title || 'Loading...'}
                        {job?.company?.name ? ` at ${job.company.name}` : ''}
                        ({statusStyle.text})
                      </option>
                    );
                  })}
                </select>
                <p className="helper-text">
                  Choose the job application you want to interview for
                </p>
              </div>

              {/* Selected Application Details */}
              {selectedApplication && selectedJob && (
                <div className="application-details">
                  <h3>Application Details</h3>
                  <div className="details-grid">
                    <div className="detail-item">
                      <strong>Position:</strong> {selectedJob.title}
                    </div>
                    <div className="detail-item">
                      <strong>Company:</strong> {selectedJob.company?.name || 'N/A'}
                    </div>
                    <div className="detail-item">
                      <strong>Location:</strong> {selectedJob.location || 'Remote'}
                    </div>
                    <div className="detail-item">
                      <strong>Status:</strong>
                      <span
                        style={{
                          background: getStatusStyle(selectedApplication.status).background,
                          color: getStatusStyle(selectedApplication.status).color,
                          padding: '2px 8px',
                          borderRadius: '4px',
                          marginLeft: '8px',
                          fontSize: '12px'
                        }}
                      >
                        {getStatusStyle(selectedApplication.status).text}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Interview Round Selector */}
              {selectedApplication && selectedJob && interviewRounds.length > 0 && (
                <div className="form-group">
                  <label htmlFor="interview-round-select">Select Interview Round:</label>
                  <select
                    id="interview-round-select"
                    value={selectedInterviewRoundIndex}
                    onChange={(e) => setSelectedInterviewRoundIndex(e.target.value)}
                    disabled={starting}
                  >
                    <option value="">-- Select an Interview Round --</option>
                    {interviewRounds.map((round, index) => (
                      <option key={index} value={index}>
                        Round {index + 1}: {round.interviewTitle || formatInterviewTypes(round.interviewTypes)}
                        {round.interviewTimeMinutes ? ` (${round.interviewTimeMinutes} min)` : ''}
                      </option>
                    ))}
                  </select>
                  <p className="helper-text">
                    Choose which interview round you want to practice
                  </p>
                </div>
              )}

              {/* Interview Round Details */}
              {selectedInterviewRound && (
                <div className="round-details">
                  <h3>Interview Round Details</h3>
                  <div className="details-grid">
                    {selectedInterviewRound.interviewTitle && (
                      <div className="detail-item">
                        <strong>Title:</strong> {selectedInterviewRound.interviewTitle}
                      </div>
                    )}
                    <div className="detail-item">
                      <strong>Interview Types:</strong> {formatInterviewTypes(selectedInterviewRound.interviewTypes)}
                    </div>
                    <div className="detail-item">
                      <strong>Duration:</strong> {selectedInterviewRound.interviewTimeMinutes || 30} minutes
                    </div>
                    <div className="detail-item">
                      <strong>Questions:</strong> {selectedInterviewRound.questions?.length || 0} prepared
                    </div>
                    {selectedInterviewRound.difficultyLevel && (
                      <div className="detail-item">
                        <strong>Difficulty:</strong> {selectedInterviewRound.difficultyLevel}
                      </div>
                    )}
                    {selectedInterviewRound.skills && selectedInterviewRound.skills.length > 0 && (
                      <div className="detail-item full-width">
                        <strong>Skills:</strong> {selectedInterviewRound.skills.join(', ')}
                      </div>
                    )}
                    {selectedInterviewRound.skillsFocusPercent !== undefined && (
                      <div className="detail-item">
                        <strong>Skills Focus:</strong> {selectedInterviewRound.skillsFocusPercent}%
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* No Interview Rounds Warning */}
              {selectedApplication && selectedJob && interviewRounds.length === 0 && (
                <div className="warning-message">
                  <p>No interview rounds are configured for this job yet. Please contact the recruiter.</p>
                </div>
              )}

              {/* Resumable Checkbox */}
              <div className="form-group checkbox-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    id="resumable-checkbox"
                    checked={resumableEnabled}
                    onChange={(e) => setResumableEnabled(e.target.checked)}
                    disabled={starting}
                  />
                  <span className="checkbox-text">
                    <strong>Enable Resume Feature</strong>
                    <span className="checkbox-subtext">
                      Allow this interview to resume from where you left off if the page is refreshed or connection is lost
                    </span>
                  </span>
                </label>
              </div>
            </div>

            <div className="actions">
              <button
                className="start-interview-btn"
                onClick={handleStartInterview}
                disabled={!selectedApplicationId || selectedInterviewRoundIndex === '' || starting}
              >
                {starting ? 'Starting Interview...' : 'Start Interview'}
              </button>
            </div>

            {selectedApplication && selectedJob && selectedInterviewRound && (
              <div className="selection-summary">
                <h3>Interview Summary:</h3>
                <p><strong>Position:</strong> {selectedJob.title}</p>
                <p><strong>Company:</strong> {selectedJob.company?.name || 'N/A'}</p>
                <p><strong>Interview Round:</strong> Round {parseInt(selectedInterviewRoundIndex, 10) + 1} - {selectedInterviewRound.interviewTitle || formatInterviewTypes(selectedInterviewRound.interviewTypes)}</p>
                <p><strong>Interview Types:</strong> {formatInterviewTypes(selectedInterviewRound.interviewTypes)}</p>
                <p><strong>Duration:</strong> {selectedInterviewRound.interviewTimeMinutes || 30} minutes</p>
                <p><strong>Resume Feature:</strong> {resumableEnabled ? 'Enabled (interview will auto-resume on refresh)' : 'Disabled'}</p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default InterviewSetup;
